import React from 'react';
import XavvyFormRenderer from '../../XavvyFormRenderer';
import { registerWidget } from '../widgetRegistry';
import { useLayoutCtx } from '../LayoutContext';
import { SimpleField } from '../../CaseClient';

// Granular Add-Case widget — title + description + priority + the case
// type's intake form (xavvy form when configured, else schema fallback).
// Reads `picked` and `form` / `intakeFormDef` / `intakeFormValues` from
// useLayoutCtx().draft and writes back through setDraft({...patch}).

const inputStyle = { padding: '7px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' };

function AddCaseFields({ config }) {
  const { token, draft, setDraft } = useLayoutCtx();
  const picked = draft?.picked || null;
  const form = draft?.form || { title: '', description: '', priority: 'medium', custom_fields: {} };
  const intakeFormDef = draft?.intakeFormDef || null;
  const intakeFormValues = draft?.intakeFormValues || {};

  const showCore = config.show_core_fields !== false;
  const showCustom = config.show_custom_fields !== false;

  if (!picked) {
    return (
      <div style={{ padding: '12px', color: 'var(--text-secondary)', fontSize: '13px' }}>
        Pick a case type first.
      </div>
    );
  }

  const pickedSchema = (() => {
    const s = picked.schema_json && typeof picked.schema_json === 'string'
      ? (() => { try { return JSON.parse(picked.schema_json); } catch { return null; } })()
      : picked.schema_json;
    return s || null;
  })();
  const customFields = pickedSchema?.fields || [];

  const patchForm = (slice) => setDraft({ form: { ...form, ...slice } });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '4px 2px' }}>
      {showCore && (
        <>
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>
              Title <span style={{ color: '#ef4444' }}>*</span>
            </span>
            <input
              value={form.title || ''}
              onChange={e => patchForm({ title: e.target.value })}
              style={{ ...inputStyle, width: '100%' }}
              placeholder="Short summary"
            />
          </label>
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>Description</span>
            <textarea
              value={form.description || ''}
              onChange={e => patchForm({ description: e.target.value })}
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical', width: '100%' }}
            />
          </label>
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>Priority</span>
            <select
              value={form.priority || 'medium'}
              onChange={e => patchForm({ priority: e.target.value })}
              style={{ ...inputStyle, width: '100%' }}
            >
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </label>
        </>
      )}
      {showCustom && intakeFormDef ? (
        <div style={{ marginTop: '6px' }}>
          <XavvyFormRenderer
            token={token}
            definition={intakeFormDef}
            initialValues={intakeFormValues}
            onValuesChange={(vals) => setDraft({ intakeFormValues: vals })}
          />
        </div>
      ) : showCustom && customFields.length > 0 && (
        <>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginTop: '6px' }}>Custom fields</div>
          {customFields.map(f => (
            <SimpleField
              key={f.key}
              field={f}
              value={form.custom_fields?.[f.key]}
              onChange={(v) => patchForm({ custom_fields: { ...(form.custom_fields || {}), [f.key]: v } })}
            />
          ))}
        </>
      )}
    </div>
  );
}

registerWidget({
  type: 'add_case.fields',
  categories: ['add_case'],
  palette_label: 'Intake fields',
  description: 'Title / description / priority + the case type\'s intake form (xavvy form when configured, else schema-driven fallback).',
  Component: AddCaseFields,
  configSchema: {
    fields: [
      { type: 'boolean', name: 'show_core_fields', label: 'Show title / description / priority', default: true },
      { type: 'boolean', name: 'show_custom_fields', label: 'Show case-type intake form / schema fields', default: true },
    ],
  },
  publishes: [],
  subscribes: [],
  group: 'Add Case',
});
